import { FC, useEffect, useState } from 'react';
import {
  Box,
  Button,
  HStack,
  Heading,
  Image,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  VStack,
  Text,
  InputGroup,
  InputLeftElement,
  Input,
  LinkBox,
  LinkOverlay,
  useDisclosure,
} from '@chakra-ui/react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import ReactPaginate from 'react-paginate';
import { useDebounce } from 'usehooks-ts';

import PlusIcon from '../../../assets/icons/plus.svg';
import {
  useGetAllCompaniesQuery,
  useGetSubscriptionPlansQuery,
} from '../../../services';
import { CenteredSpinner, FilterDrawer, RoundedBadge } from '../components';
import { companiesFilters } from '../utils';
import { ReactComponent as LeftArrowIcon } from '../../../assets/icons/left-arrow.svg';
import { ReactComponent as RightArrowIcon } from '../../../assets/icons/right-arrow.svg';
import { ReactComponent as FiltersIcon } from '../../../assets/icons/filters.svg';
import { ReactComponent as SearchIcon } from '../../../assets/icons/search.svg';

export const Companies: FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState({
    subscriptionPlan: undefined,
  });
  const debouncedSearchTerm = useDebounce<string>(searchTerm, 300);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { status } = useParams();
  const navigate = useNavigate();

  const { data, isLoading, refetch } = useGetAllCompaniesQuery({
    page,
    name: debouncedSearchTerm,
    ...filters,
  });

  const { data: subscriptionPlans } = useGetSubscriptionPlansQuery();

  useEffect(() => {
    void refetch();
  }, [debouncedSearchTerm, page, refetch]);

  const handleFilterDrawerClose = (filterValues?: {
    subscriptionPlan: string;
  }) => {
    onClose();
    if (!filterValues) return;
    console.log('filterValues', filterValues);

    setFilters({ subscriptionPlan: filterValues.subscriptionPlan });

    void refetch();
  };

  const handleRemoveFilter = () => {
    setFilters({ subscriptionPlan: undefined });

    void refetch();
  };

  const subscriptionPlan = subscriptionPlans?.data.find(
    subscriptionPlan => subscriptionPlan.id == filters.subscriptionPlan
  )?.planName;

  return (
    <Box>
      {isOpen && subscriptionPlans?.data && (
        <FilterDrawer
          isOpen={isOpen}
          onClose={handleFilterDrawerClose}
          filters={companiesFilters({
            subscriptionPlans: subscriptionPlans?.data.map(
              ({ id, planName }) => ({
                text: planName,
                value: id,
              })
            ),
          })}
        />
      )}

      <Box display="flex" flexDirection="column" flex="1">
        <HStack justify="space-between" mt="4" align="start">
          <HStack align="baseline">
            <Heading color="custom_blue.1" fontSize="2xl">
              All Companies
            </Heading>
            <Box color="custom_gray.1" fontSize="sm">
              (
              {(data ? String(data?.data.totalElements) : '0').padStart(2, '0')}{' '}
              Companies)
            </Box>
          </HStack>

          <Button
            onClick={() =>
              navigate(`/companies/${status || 'all'}/new-company`)
            }
            bg="custom_orange.2"
            _hover={{ bg: 'custom_orange.2' }}
            color="white"
            fontWeight="semibold"
            fontSize="sm"
          >
            <Image src={PlusIcon} alt="plus icon" boxSize="5" mr="1" />
            New company
          </Button>
        </HStack>

        <HStack pt="4" pb="2">
          <InputGroup flexBasis="45%">
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.300" />
            </InputLeftElement>
            <Input
              placeholder="Search"
              onChange={e => setSearchTerm(e.target.value)}
            />
          </InputGroup>

          <Button
            leftIcon={<FiltersIcon />}
            color="custom_blue.1"
            fontWeight="semibold"
            fontSize="sm"
            boxShadow="0 1px 2px 0 rgba(16, 24, 40, 0.05)"
            border="1px"
            borderColor="custom_blue.3"
            bg="none"
            _hover={{ bg: 'none' }}
            onClick={onOpen}
          >
            Filters
          </Button>
        </HStack>

        {filters.subscriptionPlan && (
          <HStack>
            <RoundedBadge
              colorSchema={subscriptionPlan}
              withCloseButton
              onClose={handleRemoveFilter}
            >
              {subscriptionPlan}
            </RoundedBadge>
          </HStack>
        )}

        {isLoading ? (
          <CenteredSpinner />
        ) : (
          <Box>
            <TableContainer
              my="4"
              boxShadow="0 1px 2px 0 rgba(16, 24, 40, 0.05)"
              borderRadius="md"
              border="1px"
              borderColor="custom_gray.3"
              minH="65vh"
              display="flex"
              flexDirection="column"
              justifyContent="space-between"
            >
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th
                      border="none"
                      color="custom_blue.1"
                      fontWeight="medium"
                      fontSize="xs"
                      letterSpacing="normal"
                      textTransform="none"
                    >
                      Company
                    </Th>
                    <Th
                      border="none"
                      color="custom_blue.1"
                      fontWeight="medium"
                      fontSize="xs"
                      letterSpacing="normal"
                      textTransform="none"
                    >
                      Status
                    </Th>
                    <Th
                      border="none"
                      color="custom_blue.1"
                      fontWeight="medium"
                      fontSize="xs"
                      letterSpacing="normal"
                      textTransform="none"
                    >
                      Activation Date
                    </Th>
                    <Th
                      border="none"
                      color="custom_blue.1"
                      fontWeight="medium"
                      fontSize="xs"
                      letterSpacing="normal"
                      textTransform="none"
                    >
                      Subscription Plan
                    </Th>
                    <Th
                      border="none"
                      color="custom_blue.1"
                      fontWeight="medium"
                      fontSize="xs"
                      letterSpacing="normal"
                      textTransform="none"
                    >
                      Users
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {data?.data.content.map(
                    ({
                      activationDate,
                      companyAvatar,
                      companyType,
                      id,
                      name,
                      numberOfProjects,
                      status,
                      subscriptionPlan,
                    }) => (
                      <LinkBox key={id} as={Tr}>
                        <Td border="none">
                          <HStack>
                            <Box
                              w="10"
                              h="10"
                              bg="custom_blue.1"
                              borderRadius="full"
                            >
                              <Image src={companyAvatar} borderRadius="full" />
                            </Box>
                            <VStack align="flex-start">
                              <Text
                                color="custom_blue.1"
                                fontWeight="semibold"
                                fontSize="sm"
                              >
                                {name}
                              </Text>
                              <Text color="custom_blue.1" fontSize="xs">
                                <LinkOverlay as={Link} to={id}>
                                  {companyType} Company
                                </LinkOverlay>
                              </Text>
                            </VStack>
                          </HStack>
                        </Td>
                        <Td border="none">
                          <RoundedBadge colorSchema={status}>
                            {status}
                          </RoundedBadge>
                        </Td>
                        <Td border="none">
                          {activationDate ? activationDate.toString() : '-'}
                        </Td>
                        <Td border="none">
                          {subscriptionPlan ? (
                            <RoundedBadge colorSchema={subscriptionPlan}>
                              subscriptionPlan
                            </RoundedBadge>
                          ) : (
                            '-'
                          )}
                        </Td>
                        <Td
                          color="custom_blue.1"
                          fontWeight="medium"
                          fontSize="xs"
                          border="none"
                        >
                          {numberOfProjects}
                        </Td>
                      </LinkBox>
                    )
                  )}
                </Tbody>
              </Table>

              <Box px="6" pb="3">
                <ReactPaginate
                  breakLabel="..."
                  pageRangeDisplayed={2}
                  // initialPage={0} // TODO: add dynamically
                  marginPagesDisplayed={3}
                  containerClassName="paginator"
                  pageClassName="paginator-item"
                  pageCount={data?.data.totalPages}
                  previousLabel={
                    <Button
                      leftIcon={<LeftArrowIcon />}
                      color="custom_blue.1"
                      fontWeight="semibold"
                      fontSize="sm"
                      boxShadow="0 1px 2px 0 rgba(16, 24, 40, 0.05)"
                      border="1px"
                      borderColor="custom_blue.3"
                      bg="none"
                      _hover={{ bg: 'none' }}
                    >
                      previous
                    </Button>
                  }
                  nextLabel={
                    <Button
                      rightIcon={<RightArrowIcon />}
                      color="custom_blue.1"
                      fontWeight="semibold"
                      fontSize="sm"
                      boxShadow="0 1px 2px 0 rgba(16, 24, 40, 0.05)"
                      border="1px"
                      borderColor="custom_blue.3"
                      bg="none"
                      _hover={{ bg: 'none' }}
                    >
                      Next
                    </Button>
                  }
                  renderOnZeroPageCount={null}
                  onPageChange={e => setPage(e.selected)}
                />
              </Box>
            </TableContainer>
          </Box>
        )}
      </Box>
    </Box>
  );
};
